import { useEffect, useState } from "react";
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton, CircularProgress } from "@mui/material";
import { TbArrowRight, TbCheck, TbClock, TbCopy, TbDownload, TbExternalLink } from "react-icons/tb";
import { MdClose } from "react-icons/md";
import { useToast } from "../../../Hooks/toast";
import {
  getOperationReceipt,
  isOperationReceiptComplete,
  mergeOperationReceipts,
  shouldShowOnChainProof,
} from "../../../Services/stablesApi";
import { formatBrl, formatStable, getAssetLabel } from "./constants";
import { formatQuotePrice } from "./stablesQuoteDisplay";
import { downloadStablesReceipt, getReceiptUrl } from "./downloadStablesReceipt";

const OPERATION_LABELS = {
  buy: "Compra",
  sell: "Venda",
  swap: "Swap",
  withdraw: "Saque",
};

const isProcessingStatus = (status) => {
  const normalized = String(status ?? "").toLowerCase();
  return normalized.includes("process") || normalized === "em_processamento";
};

const formatStatusLabel = (status) => {
  if (!status) return "—";
  if (isProcessingStatus(status)) return "Em processamento";
  if (String(status).toLowerCase().includes("conclu")) return "Concluída";
  return status;
};

const shortenHash = (hash) => {
  if (!hash || hash.length < 12) return hash;
  return `${hash.slice(0, 10)}…${hash.slice(-8)}`;
};

const ReceiptCard = ({ title, children, className = "" }) => (
  <section
    className={`rounded-xl border border-gray-100 bg-gray-50/60 p-4 dark:border-brand-dark-border dark:bg-brand-dark-elevated/60 ${className}`}
  >
    {title && (
      <h3 className="mb-3 text-[11px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">
        {title}
      </h3>
    )}
    {children}
  </section>
);

const MetaCell = ({ label, value, mono, span }) => (
  <div className={span ? "sm:col-span-2" : ""}>
    <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">{label}</p>
    <p
      className={`mt-1 text-sm font-semibold text-brand-dark dark:text-gray-100 ${
        mono ? "break-all font-mono text-xs" : ""
      }`}
    >
      {value || "—"}
    </p>
  </div>
);

const AmountLine = ({ label, value, primary }) => (
  <div className={`flex items-center justify-between gap-3 ${primary ? "py-1" : "py-0.5"}`}>
    <span className={`text-sm ${primary ? "font-medium text-gray-600 dark:text-gray-300" : "text-gray-500 dark:text-gray-400"}`}>
      {label}
    </span>
    <span
      className={`text-right font-bold ${
        primary
          ? "text-lg text-brand-dark dark:text-gray-50"
          : "text-sm text-gray-700 dark:text-gray-200"
      }`}
    >
      {value || "—"}
    </span>
  </div>
);

const FeeLine = ({ label, value }) => (
  <div className="flex items-center justify-between gap-3">
    <span className="text-xs text-gray-400 dark:text-gray-500">{label}</span>
    <span className="text-right text-xs text-gray-400 dark:text-gray-500">{value}</span>
  </div>
);

const FeesInline = ({ receipt }) => {
  const hasFees = receipt.feePercent > 0 || receipt.networkFeeStable > 0;
  if (!hasFees) return null;

  return (
    <div className="mt-2 space-y-1 border-t border-gray-100 pt-2 dark:border-brand-dark-border/60">
      {receipt.feePercent > 0 && (
        <FeeLine
          label={`Taxa (${receipt.feePercent}%)`}
          value={
            receipt.operation === "swap"
              ? formatStable(receipt.feeStable, receipt.toAssetId || receipt.assetId)
              : formatBrl(receipt.feeBrl)
          }
        />
      )}
      {receipt.networkFeeStable > 0 && (
        <FeeLine
          label="Taxa da rede"
          value={formatStable(receipt.networkFeeStable, receipt.assetId)}
        />
      )}
    </div>
  );
};

const ReceiptHero = ({ isProcessing, operationLabel, statusLabel }) => (
  <div className="flex flex-col items-center pb-1 pt-2 text-center">
    <span
      className={`inline-flex h-16 w-16 items-center justify-center rounded-full ring-4 ${
        isProcessing
          ? "bg-amber-50 text-amber-600 ring-amber-100 dark:bg-amber-950/50 dark:text-amber-400 dark:ring-amber-900/40"
          : "bg-emerald-50 text-emerald-600 ring-emerald-100 dark:bg-emerald-950/50 dark:text-emerald-400 dark:ring-emerald-900/40"
      }`}
    >
      {isProcessing ? <TbClock className="h-8 w-8" /> : <TbCheck className="h-8 w-8" strokeWidth={2.5} />}
    </span>
    <p className="mt-4 text-lg font-bold text-brand-dark dark:text-gray-100">{operationLabel}</p>
    <span
      className={`mt-2 inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
        isProcessing
          ? "bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300"
          : "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300"
      }`}
    >
      {statusLabel}
    </span>
  </div>
);

const buildReceiptDownloadItem = (receipt) => ({
  id: receipt.transactionId,
  receiptUrl: getReceiptUrl(receipt),
});

const OnChainHashCompact = ({ receipt, isProcessing, onCopy }) => {
  if (!receipt.txHash) {
    const pendingMessage =
      receipt.operation === "withdraw" && isProcessing
        ? "O hash on-chain será gerado após o processamento do saque."
        : "Transação ainda não confirmada on-chain.";

    return (
      <div className="sm:col-span-2">
        <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
          Transaction hash
        </p>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{pendingMessage}</p>
      </div>
    );
  }

  return (
    <div className="sm:col-span-2">
      <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
        Transaction hash
      </p>
      <div className="mt-1 flex items-center gap-2">
        <code className="min-w-0 flex-1 truncate font-mono text-xs font-semibold text-brand-dark dark:text-gray-100">
          {shortenHash(receipt.txHash)}
        </code>
        <button
          type="button"
          className="btn-outline-icon !shrink-0 dark:border-brand-dark-border dark:bg-brand-dark-surface dark:text-gray-100"
          onClick={onCopy}
          title="Copiar hash"
        >
          <TbCopy className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

const WithdrawConsolidatedCard = ({
  receipt,
  statusLabel,
  showOnChainProof,
  isProcessing,
  onCopyHash,
}) => (
  <ReceiptCard title="Detalhes da transação">
    <div className="grid gap-3 sm:grid-cols-2">
      <MetaCell label="ID da transação" value={receipt.transactionId} mono span />
      <MetaCell label="Data" value={receipt.date} />
      <MetaCell label="Status" value={statusLabel} />
      {receipt.paymentMethod && <MetaCell label="Forma de pagamento" value={receipt.paymentMethod} />}

      <div className="space-y-0.5 sm:col-span-2 sm:border-t sm:border-gray-100 sm:pt-3 dark:sm:border-brand-dark-border/60">
        <AmountLine label="Valor sacado" value={formatStable(receipt.stableAmount, receipt.assetId)} />
        <AmountLine
          label="Valor líquido"
          value={formatStable(receipt.netStableAmount, receipt.assetId)}
          primary
        />
        <FeesInline receipt={receipt} />
      </div>

      {receipt.destination && <MetaCell label="Destino" value={receipt.destination} mono span />}

      {showOnChainProof && (
        <>
          {receipt.chain && <MetaCell label="Rede (chain)" value={receipt.chain} />}
          <OnChainHashCompact receipt={receipt} isProcessing={isProcessing} onCopy={onCopyHash} />
        </>
      )}
    </div>
  </ReceiptCard>
);

export const StablesOperationReceiptModal = ({ open, onClose, receipt: initialReceipt }) => {
  const toast = useToast();
  const [receipt, setReceipt] = useState(initialReceipt);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const displayReceipt = receipt ?? initialReceipt;

  useEffect(() => {
    if (!open) {
      setReceipt(null);
      setLoading(false);
      return;
    }
    setReceipt(initialReceipt);
  }, [open, initialReceipt]);

  useEffect(() => {
    if (!open || !initialReceipt?.transactionId) return;
    if (isOperationReceiptComplete(initialReceipt)) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    getOperationReceipt(initialReceipt.transactionId)
      .then((detail) => {
        if (!cancelled && detail?.transactionId) {
          setReceipt((prev) => mergeOperationReceipts(prev ?? initialReceipt, detail));
        }
      })
      .catch(() => {
        if (!cancelled) setReceipt(initialReceipt);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, initialReceipt]);

  if (!open || !displayReceipt) return null;

  const operationLabel = OPERATION_LABELS[displayReceipt.operation] || "Operação";
  const receiptUrl = getReceiptUrl(displayReceipt);
  const hasReceipt = Boolean(receiptUrl);
  const isProcessing = isProcessingStatus(displayReceipt.status);
  const statusLabel = formatStatusLabel(displayReceipt.status);
  const showOnChainProof = shouldShowOnChainProof({
    operation: displayReceipt.operation,
    hasOnChainProof: displayReceipt.hasOnChainProof,
    txHash: displayReceipt.txHash,
  });

  const showLoadingOnly = loading && !isOperationReceiptComplete(displayReceipt);

  const handleCopyHash = async () => {
    if (!displayReceipt.txHash) return;

    try {
      await navigator.clipboard.writeText(displayReceipt.txHash);
      toast.addToast({
        type: "success",
        title: "Hash copiado",
        description: "Transaction hash copiado para a área de transferência.",
      });
    } catch {
      toast.addToast({ type: "error", title: "Erro ao copiar", description: "Não foi possível copiar o hash." });
    }
  };

  const handleDownloadReceipt = async () => {
    if (!hasReceipt) return;

    setDownloading(true);
    try {
      await downloadStablesReceipt(buildReceiptDownloadItem(displayReceipt));
      toast.addToast({
        type: "success",
        title: "Comprovante baixado",
        description: "O PDF foi salvo com sucesso.",
      });
    } catch {
      toast.addToast({
        type: "error",
        title: "Erro ao baixar",
        description: "Não foi possível baixar o comprovante. Tente novamente.",
      });
    } finally {
      setDownloading(false);
    }
  };

  const amountCard = () => {
    if (displayReceipt.operation === "buy") {
      return (
        <ReceiptCard title="Valores">
          <AmountLine label="Valor pago (BRL)" value={formatBrl(displayReceipt.brlAmount)} />
          <div className="my-3 border-t border-[#e7a73f]/20 dark:border-brand-gold-mid/20" />
          <AmountLine
            label="Stablecoin recebida"
            value={formatStable(displayReceipt.netStableAmount, displayReceipt.assetId)}
            primary
          />
          <FeesInline receipt={displayReceipt} />
        </ReceiptCard>
      );
    }

    if (displayReceipt.operation === "sell") {
      return (
        <ReceiptCard title="Valores">
          <AmountLine
            label="Stablecoin vendida"
            value={formatStable(displayReceipt.stableAmount, displayReceipt.assetId)}
          />
          <div className="my-3 border-t border-[#e7a73f]/20 dark:border-brand-gold-mid/20" />
          <AmountLine label="Valor creditado (BRL)" value={formatBrl(displayReceipt.netBrlAmount)} primary />
          <FeesInline receipt={displayReceipt} />
        </ReceiptCard>
      );
    }

    if (displayReceipt.operation === "swap") {
      const fromLabel =
        displayReceipt.fromAssetLabel || getAssetLabel(displayReceipt.fromAssetId) || "—";
      const toLabel =
        displayReceipt.toAssetLabel || getAssetLabel(displayReceipt.toAssetId) || "—";
      const sent = formatStable(displayReceipt.fromAmount, displayReceipt.fromAssetId);
      const received = formatStable(displayReceipt.netToAmount, displayReceipt.toAssetId);
      return (
        <ReceiptCard title="Valores">
          <p className="mb-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400">
            Origem <span className="font-semibold text-brand-dark dark:text-gray-200">{fromLabel}</span>
            {" → "}
            Destino <span className="font-semibold text-brand-dark dark:text-gray-200">{toLabel}</span>
          </p>
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1 text-center">
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">Enviado</p>
              <p className="mt-1 truncate text-sm font-semibold text-gray-700 dark:text-gray-200">{sent}</p>
            </div>
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#e7a73f]/15 text-[#c8872a] dark:bg-brand-gold-mid/15 dark:text-brand-gold-mid">
              <TbArrowRight className="h-4 w-4" />
            </span>
            <div className="min-w-0 flex-1 text-center">
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">Recebido</p>
              <p className="mt-1 truncate text-base font-bold text-brand-dark dark:text-gray-50">{received}</p>
            </div>
          </div>
          <FeesInline receipt={displayReceipt} />
        </ReceiptCard>
      );
    }

    if (displayReceipt.operation === "withdraw") {
      return (
        <ReceiptCard title="Valores">
          <AmountLine
            label="Valor sacado"
            value={formatStable(displayReceipt.stableAmount, displayReceipt.assetId)}
          />
          <div className="my-3 border-t border-[#e7a73f]/20 dark:border-brand-gold-mid/20" />
          <AmountLine
            label="Valor líquido"
            value={formatStable(displayReceipt.netStableAmount, displayReceipt.assetId)}
            primary
          />
          <FeesInline receipt={displayReceipt} />
          {displayReceipt.destination && (
            <>
              <div className="my-3 border-t border-gray-200/80 dark:border-brand-dark-border" />
              <MetaCell label="Destino" value={displayReceipt.destination} mono />
            </>
          )}
        </ReceiptCard>
      );
    }

    return null;
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle className="!relative !pr-12 !text-center !text-sm !font-semibold !text-gray-500 dark:!text-gray-400">
        Comprovante
        <IconButton
          aria-label="Fechar"
          onClick={onClose}
          size="small"
          className="!absolute !right-2 !top-2 dark:!text-gray-300"
        >
          <MdClose />
        </IconButton>
      </DialogTitle>

      <DialogContent className="!pt-0">
        {showLoadingOnly ? (
          <div className="flex justify-center py-12">
            <CircularProgress size={28} className="!text-[#e7a73f]" />
          </div>
        ) : (
          <div className="space-y-4">
            <ReceiptHero
              isProcessing={isProcessing}
              operationLabel={operationLabel}
              statusLabel={statusLabel}
            />

            {displayReceipt.operation === "withdraw" ? (
              <WithdrawConsolidatedCard
                receipt={displayReceipt}
                statusLabel={statusLabel}
                showOnChainProof={showOnChainProof}
                isProcessing={isProcessing}
                onCopyHash={handleCopyHash}
              />
            ) : (
              <>
                <ReceiptCard title="Detalhes da transação">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <MetaCell label="ID da transação" value={displayReceipt.transactionId} mono span />
                    <MetaCell label="Data" value={displayReceipt.date} />
                    <MetaCell label="Status" value={statusLabel} />
                    {displayReceipt.paymentMethod && (
                      <MetaCell label="Forma de pagamento" value={displayReceipt.paymentMethod} />
                    )}
                  </div>
                </ReceiptCard>

                {amountCard()}

                {displayReceipt.rate > 0 && displayReceipt.operation !== "swap" && (
                  <ReceiptCard>
                    <AmountLine
                      label="Cotação"
                      value={`1 ${displayReceipt.assetLabel || displayReceipt.assetId?.toUpperCase()} = ${formatQuotePrice(displayReceipt.rate)}`}
                    />
                  </ReceiptCard>
                )}

                {showOnChainProof && (
                  <ReceiptCard title="Comprovação on-chain">
                    {displayReceipt.chain && <MetaCell label="Rede (chain)" value={displayReceipt.chain} />}
                    {displayReceipt.txHash ? (
                      <div className={displayReceipt.chain ? "mt-4" : ""}>
                        <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
                          Transaction hash
                        </p>
                        <div className="mt-2 flex items-start gap-2">
                          <code className="stables-code-block min-w-0 flex-1 dark:border-brand-dark-border dark:bg-brand-dark-surface dark:text-gray-100">
                            {displayReceipt.txHash}
                          </code>
                          <button
                            type="button"
                            className="btn-outline-icon !shrink-0 dark:border-brand-dark-border dark:bg-brand-dark-surface dark:text-gray-100"
                            onClick={handleCopyHash}
                            title="Copiar hash"
                          >
                            <TbCopy className="h-4 w-4" />
                          </button>
                        </div>
                        {displayReceipt.explorerUrl && (
                          <p className="mt-2 text-[11px] text-gray-400 dark:text-gray-300">
                            Resumo: {shortenHash(displayReceipt.txHash)} em {displayReceipt.chain}
                          </p>
                        )}
                      </div>
                    ) : null}
                  </ReceiptCard>
                )}
              </>
            )}
          </div>
        )}
      </DialogContent>

      <DialogActions className="!flex !flex-wrap !justify-end !gap-2 !border-t !border-gray-100 !px-6 !pb-4 !pt-3 dark:!border-brand-dark-border">
        <button
          type="button"
          className="btn-outline dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-100 dark:hover:bg-[#1f1f1f] disabled:dark:opacity-50"
          onClick={handleDownloadReceipt}
          disabled={downloading || !hasReceipt || showLoadingOnly}
          title={!hasReceipt ? "Comprovante indisponível" : undefined}
        >
          <TbDownload className="h-4 w-4" />
          {downloading ? "Baixando..." : "Baixar comprovante"}
        </button>

        {showOnChainProof && displayReceipt.explorerUrl ? (
          <a
            href={displayReceipt.explorerUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary no-underline"
          >
            <TbExternalLink className="h-4 w-4" />
            Abrir na blockchain
          </a>
        ) : (
          <button type="button" className="btn-primary" onClick={onClose}>
            Fechar
          </button>
        )}
      </DialogActions>
    </Dialog>
  );
};
